/**
 * useRealtimeChannel — generischer, resilienter Realtime-Wrapper.
 *
 * Verhalten:
 * - Stille Wiederherstellung: Reconnect mit exponentiellem Backoff (1s → 2s → 4s …, capped 30s, jitter).
 * - Tab-/Netz-aware: pausiert offline und im Background-Tab, reconnectet sofort beim Aufwachen.
 * - Polling-Fallback: nach `maxAttempts` (default 6) wechselt der Hook in einen Polling-Modus
 *   und ruft `onPollingTick` periodisch auf — typische Anwendung: React-Query `invalidateQueries`.
 * - Topic-Konvention (Schritt 04 Pre-Open-Beta): Topic muss mit `pub:` / `user:<uid>:` /
 *   `org:<id>:` / `admin:` beginnen; sonst Dev-Warning.
 *
 * Bewusst einfach gehalten — keine UI-Effekte, kein globaler Store. Konsumenten können den
 * Status (`status`, `attempt`, `polling`) lesen, müssen aber nicht.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export type RealtimeStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'polling-fallback'
  | 'closed';

type PostgresFilter = {
  event: '*' | 'INSERT' | 'UPDATE' | 'DELETE';
  schema: string;
  table: string;
  filter?: string;
};

export interface RealtimeChannelBinding {
  /** Postgres CDC binding. */
  postgresChanges: PostgresFilter;
  /** Handler invoked for every matching event. */
  handler: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;
}

export interface UseRealtimeChannelOptions {
  /** Topic name. Must follow `pub:* | user:<uid>:* | org:<id>:* | admin:*`. */
  topic: string;
  /** One or more postgres_changes bindings. */
  bindings: RealtimeChannelBinding[];
  /** Whether the channel should be active. Default: true. */
  enabled?: boolean;
  /** Max reconnect attempts before switching to polling. Default: 6. */
  maxAttempts?: number;
  /** Polling tick called every `pollingIntervalMs` while in fallback. */
  onPollingTick?: () => void;
  /** Polling cadence in ms. Default: 30_000. */
  pollingIntervalMs?: number;
  /** Optional error callback for telemetry. */
  onError?: (info: { reason: string; attempt: number }) => void;
}

export interface UseRealtimeChannelResult {
  status: RealtimeStatus;
  attempt: number;
  polling: boolean;
}

const TOPIC_PREFIXES = ['pub:', 'user:', 'org:', 'admin:'] as const;

function isValidTopic(topic: string): boolean {
  return TOPIC_PREFIXES.some(p => topic.startsWith(p));
}

function backoffDelay(attempt: number): number {
  // 1s, 2s, 4s, 8s, 16s, 30s (capped) + ±20% jitter
  const base = Math.min(30_000, 1_000 * 2 ** attempt);
  const jitter = base * 0.2 * (Math.random() * 2 - 1);
  return Math.max(500, Math.round(base + jitter));
}

export function useRealtimeChannel(options: UseRealtimeChannelOptions): UseRealtimeChannelResult {
  const {
    topic,
    bindings,
    enabled = true,
    maxAttempts = 6,
    onPollingTick,
    pollingIntervalMs = 30_000,
    onError,
  } = options;

  const [status, setStatus] = useState<RealtimeStatus>('idle');
  const [attempt, setAttempt] = useState(0);
  const [polling, setPolling] = useState(false);

  // Hold latest handlers/options in refs so reconnect logic doesn't re-run on every render.
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;
  const onPollingTickRef = useRef(onPollingTick);
  onPollingTickRef.current = onPollingTick;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Stable signature so we only re-subscribe when the topic / binding shape actually changes
  // (not when handler identities change).
  const bindingsSignature = useMemo(
    () => JSON.stringify(bindings.map(b => b.postgresChanges)),
    [bindings],
  );

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return;
    }

    if (!isValidTopic(topic) && import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[useRealtimeChannel] Topic "${topic}" violates the pub:/user:/org:/admin: convention. ` +
          'Server policies will reject the subscription.',
      );
    }

    let activeChannel: RealtimeChannel | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let currentAttempt = 0;
    let cancelled = false;
    // Re-entrancy guard: `supabase.removeChannel()` synchronously fires the
    // channel's CLOSED event, which re-enters our subscribe callback. Without
    // this flag the CLOSED handler would call `scheduleReconnect` → which
    // calls `teardownChannel()` → `removeChannel()` → CLOSED again, recursing
    // until the JS engine throws "Maximum call stack size exceeded".
    let tearingDown = false;
    let reconnecting = false;

    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };
    const clearPollTimer = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };
    const teardownChannel = () => {
      if (!activeChannel || tearingDown) return;
      const ch = activeChannel;
      // Null the ref FIRST so any synchronous CLOSED callback fired by
      // removeChannel() sees no active channel and bails out.
      activeChannel = null;
      tearingDown = true;
      try {
        supabase.removeChannel(ch);
      } catch {
        /* ignore */
      } finally {
        tearingDown = false;
      }
    };

    const enterPollingFallback = (reason: string) => {
      if (cancelled) return;
      teardownChannel();
      clearReconnectTimer();
      setPolling(true);
      setStatus('polling-fallback');
      onErrorRef.current?.({ reason, attempt: currentAttempt });
      // Trigger one immediate refresh, then keep polling.
      onPollingTickRef.current?.();
      clearPollTimer();
      pollTimer = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        onPollingTickRef.current?.();
      }, pollingIntervalMs);
    };

    const scheduleReconnect = (reason: string) => {
      if (cancelled) return;
      // Suppress re-entrant calls fired by the synchronous CLOSED event that
      // teardownChannel()/removeChannel() emit while tearing the socket down.
      if (tearingDown || reconnecting) return;
      reconnecting = true;
      teardownChannel();
      currentAttempt += 1;
      setAttempt(currentAttempt);
      onErrorRef.current?.({ reason, attempt: currentAttempt });
      if (currentAttempt > maxAttempts) {
        enterPollingFallback(`max-attempts:${reason}`);
        reconnecting = false;
        return;
      }
      setStatus('reconnecting');
      const delay = backoffDelay(currentAttempt);
      clearReconnectTimer();
      reconnectTimer = setTimeout(() => {
        reconnecting = false;
        if (cancelled) return;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        connect();
      }, delay);
    };

    const connect = () => {
      if (cancelled) return;
      teardownChannel();
      clearPollTimer();
      setPolling(false);
      setStatus(prev => (prev === 'connected' ? 'connected' : 'connecting'));

      const ch = supabase.channel(topic);
      for (const b of bindingsRef.current) {
        // Supabase typings for `.on('postgres_changes', filter, cb)` use a string overload.
        // Cast filter to the expected shape; handler stays strongly typed via our ref.
        ch.on(
          'postgres_changes' as never,
          b.postgresChanges as never,
          (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => {
            // Always invoke the latest handler.
            const latest = bindingsRef.current.find(
              x =>
                x.postgresChanges.table === b.postgresChanges.table &&
                x.postgresChanges.event === b.postgresChanges.event &&
                x.postgresChanges.filter === b.postgresChanges.filter,
            );
            (latest ?? b).handler(payload);
          },
        );
      }

      ch.subscribe(realtimeStatus => {
        if (cancelled) return;
        switch (realtimeStatus) {
          case 'SUBSCRIBED':
            currentAttempt = 0;
            setAttempt(0);
            setPolling(false);
            clearPollTimer();
            setStatus('connected');
            break;
          case 'CHANNEL_ERROR':
            scheduleReconnect('channel-error');
            break;
          case 'TIMED_OUT':
            scheduleReconnect('timed-out');
            break;
          case 'CLOSED':
            // Closed without explicit cancel: try to reconnect.
            if (!cancelled) scheduleReconnect('closed');
            break;
          default:
            break;
        }
      });

      activeChannel = ch;
    };

    const handleVisibility = () => {
      if (cancelled) return;
      if (document.visibilityState === 'visible') {
        // Wake-up: drop any pending backoff and reconnect immediately if we're not connected.
        if (status !== 'connected') {
          currentAttempt = 0;
          setAttempt(0);
          clearReconnectTimer();
          connect();
        }
      }
    };
    const handleOnline = () => {
      if (cancelled) return;
      currentAttempt = 0;
      setAttempt(0);
      clearReconnectTimer();
      connect();
    };
    const handleOffline = () => {
      // Don't tear down — Supabase JS handles socket loss; we just stop scheduling.
      clearReconnectTimer();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    connect();

    return () => {
      cancelled = true;
      clearReconnectTimer();
      clearPollTimer();
      teardownChannel();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (isMountedRef.current) setStatus('closed');
    };
    // We intentionally exclude `status` from deps — it's only read inside event handlers
    // and re-subscribing on every status change would cause loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, bindingsSignature, enabled, maxAttempts, pollingIntervalMs]);

  return { status, attempt, polling };
}